//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';
import {
  Language as WebsiteIcon,
  LibraryBooks as DocsIcon,
  TextSnippet as TextSnippetIcon
} from '@mui/icons-material';
import { ERoutesRoot } from '@/routes/RoutesEnum';

// Items with no category appear in the top section.  The 'type' can be
// "internal" or "external" indicating if the link is a React page or an
// external website.  Links to URLs hosted on the backed are "external".
export interface DocumentationLink {
  id: string;
  title: string;
  description: string;
  url: string;
  icon: React.ReactNode;
  type: 'internal' | 'external';
  category?: string;
}

const documentationLinks: DocumentationLink[] = [
  {
    id: 'user-guide',
    title: 'User Guide',
    description: 'Guide for installing and using Mission Control',
    url: 'https://github.com/Rocketgraphai/rocketgraph/blob/main/' +
         'README.md#rocketgraph-mission-control',
    icon: <DocsIcon />,
    type: 'external'
  },
  {
    id: 'xgt-server-docs',
    title: 'xGT Server Documentation',
    description:
      'Comprehensive guides and API documentation to help you start ' +
      'working with xGT as quickly as possible',
    url: '/documentation/',
    icon: <DocsIcon />,
    category: 'Advanced Resources',
    type: 'external'
  },
  {
    id: 'website',
    title: 'Company Website',
    description: 'Official Rocketgraph website',
    url: 'https://www.rocketgraph.com/',
    icon: <WebsiteIcon />,
    type: 'external'
  },
  {
    id: 'launch-notes',
    title: 'Mission Control Launch Notes',
    description: 'Launch notes for Mission Control',
    url: ERoutesRoot.LaunchNotes,
    icon: <TextSnippetIcon />,
    category: 'Launch Notes',
    type: 'internal'
  },
  {
    id: 'xgt-launch-notes',
    title: 'xGT Launch Notes',
    description: 'Launch notes for the xGT server',
    url: '/documentation/launch_notes/index.html',
    icon: <TextSnippetIcon />,
    category: 'Launch Notes',
    type: 'external'
  },
];

// Get documentation links filtered by category.  A null category returns all
// links.
export const getDocumentationLinks = (
  category: string | null = null
): DocumentationLink[] => {
  if (category)
    return documentationLinks.filter(link => link.category === category);

  return documentationLinks.filter(link => !link.category);
};

export const getDocumentationCategories = (): string[] => {
  const categories = new Set<string>();
  documentationLinks.forEach(link => {
    if (link.category)
      categories.add(link.category);
  });

  return Array.from(categories);
};
